import DataTable from "@/components/Common/DataTable";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { bidColumn } from "../columns/bidColumn";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormLabel } from "@/components/ui/form";
import { useState, useEffect, useRef } from "react";
import ControlledInput from "@/components/Common/form/ControlledInput";
import ControlledDatePicker from "@/components/Common/form/ControlledDatePicker";
import { Search } from "lucide-react";
import ControlledRadioGroup from "@/components/Common/form/ControlledRadioGroup";
import { format, subDays, subMonths, subYears } from "date-fns";
import { useParams } from "react-router-dom";
import {
  useResetSearchProjectGroupsBidNotice,
  useSaveProjectGroupsBidNotice,
  useSearchProjectGroupsBidNotice,
} from "@/libs/react-query/hooks/useProjectQuery";
import { ProjectType } from "@/consts/Project";
import { Skeleton } from "@/components/ui/skeleton";
import { showToast } from "@/utils/toastUtils";
import { DEFAULT_PAGINATION } from "@/consts/common";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type Props = {
  type: ProjectType;
  trigger: React.ReactNode;
  defaultTitle: string;
};

const filterSchema = z.object({
  keyword: z.string(),
  dateRangeType: z.enum(["7days", "30days", "1year", "custom"]),
  customStartDate: z.coerce.string().optional(),
  customEndDate: z.coerce.string().optional(),
});

type FilterFormValues = z.infer<typeof filterSchema>;

export default function ProjectDetailBidNoticeDialog({
  type,
  trigger,
  defaultTitle,
}: Props) {
  const [open, setOpen] = useState(false);
  const [selectedBidId, setSelectedBidId] = useState<string | null>(null);
  const [searchType, setSearchType] = useState<"title" | "number">("title");

  const { group_id = "" } = useParams();

  const form = useForm<FilterFormValues>({
    resolver: zodResolver(filterSchema),
    defaultValues: {
      keyword: defaultTitle,
      dateRangeType: "30days",
    },
  });
  const { watch, handleSubmit, setValue, getValues } = form;

  const isAutoUpdate = useRef(false);
  const isMounted = useRef(false);

  const [watchedKeyword, dateRangeType, customStartDate, customEndDate] = watch(
    ["keyword", "dateRangeType", "customStartDate", "customEndDate"]
  );

  const searchParams = {
    project_type: type,
    start_date: customStartDate
      ? new Date(customStartDate).toISOString()
      : null,
    end_date: customEndDate ? new Date(customEndDate).toISOString() : null,
    pagination: DEFAULT_PAGINATION,
    ...(searchType === "title"
      ? { title_keyword: watchedKeyword }
      : { notice_no_keyword: watchedKeyword }),
  };

  const { data, refetch, isLoading, isFetching } =
    useSearchProjectGroupsBidNotice(group_id, searchParams);
  const { mutate } = useSaveProjectGroupsBidNotice(group_id);
  const resetSearchProjectGroupsBidNotice =
    useResetSearchProjectGroupsBidNotice(group_id);

  useEffect(() => {
    if (dateRangeType === "custom") return;

    const today = new Date();
    let startDate = today;

    switch (dateRangeType) {
      case "7days":
        startDate = subDays(today, 7);
        break;
      case "30days":
        startDate = subMonths(today, 1);
        break;
      case "1year":
        startDate = subYears(today, 1);
        break;
    }

    isAutoUpdate.current = true;

    setValue("customStartDate", format(startDate, "yyyy-MM-dd"));
    setValue("customEndDate", format(today, "yyyy-MM-dd"));
  }, [dateRangeType, setValue]);

  useEffect(() => {
    if (defaultTitle && getValues("keyword") !== defaultTitle) {
      setValue("keyword", defaultTitle);
    }
  }, [defaultTitle]);

  useEffect(() => {
    if (isAutoUpdate.current) {
      isAutoUpdate.current = false;
      return;
    }

    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }

    const currentDateRangeType = getValues("dateRangeType");

    if (
      currentDateRangeType !== "custom" &&
      (customStartDate || customEndDate)
    ) {
      setValue("dateRangeType", "custom");
    }
  }, [customStartDate, customEndDate, setValue, getValues]);

  const onDialogChange = (value: boolean) => {
    if (!value) {
      form.reset();
      setSelectedBidId(null);
      resetSearchProjectGroupsBidNotice(); // 검색 쿼리 리셋
    }
    setOpen(value);
  };

  const handleSearch = (data: FilterFormValues) => {
    refetch();
  };

  const handleSave = () => {
    if (!selectedBidId) return;

    mutate(
      {
        project_id: selectedBidId,
        project_type: type,
      },
      {
        onSuccess: () => {
          showToast({
            message: "공고 추가가 완료되었습니다",
          });
          onDialogChange(false);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onDialogChange}>
      <DialogTrigger>{trigger}</DialogTrigger>
      <DialogContent
        className="max-w-[1050px]"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>공고 찾기</DialogTitle>
          <DialogDescription />
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={handleSubmit(handleSearch)}>
            <div>
              <div className="w-full pb-6 mb-6 border-b border-b-gray-D flex items-start gap-x-3">
                <div className="flex-1 flex flex-col gap-2">
                  <FormLabel className="text-gray-B text-xs font-bold">
                    공고 제목
                  </FormLabel>
                  <div className="flex items-center mt-2 gap-2">
                    {type === ProjectType.PROJ && (
                      <Select
                        onValueChange={(val) =>
                          setSearchType(val as "title" | "number")
                        }
                        value={searchType}
                      >
                        <SelectTrigger className="w-fit gap-2">
                          <SelectValue placeholder="선택" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="title">공고 제목</SelectItem>
                          <SelectItem value="number">공고 번호</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    <ControlledInput
                      name="keyword"
                      placeholder="공고 제목을 입력해주세요."
                      noLabel
                      containerClassName="flex-1"
                    />
                  </div>
                </div>
                <div>
                  <div className="flex items-end gap-2">
                    <ControlledDatePicker
                      name="customStartDate"
                      label="공고 게시일"
                      placeholder="날짜를 선택하세요"
                      labelClassName="text-gray-B text-xs font-bold"
                      triggerClassName="min-w-36 h-10 px-2 py-1"
                      iconClassName="text-gray-B"
                      placeholderClassName="text-xs"
                    />
                    <p className="pb-3 text-[10px] text-gray-B font-bold">~</p>
                    <ControlledDatePicker
                      name="customEndDate"
                      label=""
                      placeholder="날짜를 선택하세요"
                      labelClassName="text-gray-B text-xs font-bold"
                      triggerClassName="min-w-36 h-10 px-2 py-1"
                      iconClassName="text-gray-B"
                      placeholderClassName="text-xs"
                    />
                  </div>
                  <ControlledRadioGroup
                    label=""
                    name="dateRangeType"
                    options={[
                      { label: "일주일 전", value: "7days" },
                      { label: "한 달 전", value: "30days" },
                      { label: "일 년 전", value: "1year" },
                      { label: "자유 입력", value: "custom" },
                    ]}
                    containerClassName="grid grid-cols-4 mt-2"
                    textClassName="text-gray-B text-[10px] font-semibold"
                  />
                </div>
                <Button
                  type="submit"
                  className="mt-8 h-10 bg-dark-brown hover:bg-dark-brown/80"
                >
                  <Search className="w-3 h-3" />
                  검색하기
                </Button>
              </div>
              {isLoading || isFetching ? (
                <Skeleton className="h-80 w-full rounded-md" />
              ) : (
                <DataTable
                  columns={bidColumn}
                  data={data?.projects || []}
                  pageSize={10}
                  onRowSelect={(row) => {
                    setSelectedBidId(row?.project_id ?? null);
                  }}
                />
              )}
            </div>
            <DialogFooter className="mt-4">
              <Button
                type="button"
                variant="cancel"
                onClick={() => onDialogChange(false)}
              >
                취소하기
              </Button>
              <Button
                type="button"
                variant="confirm"
                disabled={!selectedBidId}
                onClick={handleSave}
              >
                추가하기
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
